import { useState, useEffect } from 'react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Progress } from '../../components/ui/progress';
import { Badge } from '../../components/ui/badge';
import { 
  CheckCircle, 
  ArrowRight, 
  Clock, 
  FileCheck, 
  AlertTriangle, 
  Target, 
  Sparkles,
  ChevronLeft,
  X,
  Loader2
} from 'lucide-react';
import { leadsApi } from '../../lib/api';
import { toast } from 'sonner';

// Quiz questions data - Open-ended questions for text answers
const quizQuestions = [
  // Teil 1: Allgemeine Fragen (einfach – A2)
  { id: 1, level: 'A2', teil: 1, question: "Wie heißt du und woher kommst du?", type: 'text' },
  { id: 2, level: 'A2', teil: 1, question: "Was machst du in deiner Freizeit?", type: 'text' },
  { id: 3, level: 'A2', teil: 1, question: "Beschreibe deinen Tagesablauf.", type: 'text' },
  { id: 4, level: 'A2', teil: 1, question: "Was ist dein Lieblingsessen und warum?", type: 'text' },
  { id: 5, level: 'A2', teil: 1, question: "Hast du Geschwister? Erzähle kurz von deiner Familie.", type: 'text' },
  // Teil 2: Alltag und Erfahrungen (A2–B1)
  { id: 6, level: 'A2-B1', teil: 2, question: "Was hast du letztes Wochenende gemacht?", type: 'text' },
  { id: 7, level: 'A2-B1', teil: 2, question: "Beschreibe deine Schule oder Universität.", type: 'text' },
  { id: 8, level: 'A2-B1', teil: 2, question: "Welche Verkehrsmittel benutzt du normalerweise? Warum?", type: 'text' },
  { id: 9, level: 'A2-B1', teil: 2, question: "Erzähle von deinem letzten Urlaub.", type: 'text' },
  { id: 10, level: 'A2-B1', teil: 2, question: "Was machst du, um Stress zu vermeiden?", type: 'text' },
  // Teil 3: Meinung und Argumentation (B1–B2)
  { id: 11, level: 'B1-B2', teil: 3, question: "Was denkst du über soziale Medien? Nenne Vor- und Nachteile.", type: 'text' },
  { id: 12, level: 'B1-B2', teil: 3, question: "Ist Sport wichtig im Leben? Warum (nicht)?", type: 'text' },
  { id: 13, level: 'B1-B2', teil: 3, question: "Sollte man im Ausland studieren? Begründe deine Meinung.", type: 'text' },
  { id: 14, level: 'B1-B2', teil: 3, question: "Welche Rolle spielt Umweltschutz heute?", type: 'text' },
  { id: 15, level: 'B1-B2', teil: 3, question: "Was sind die Vorteile und Nachteile von Online-Unterricht?", type: 'text' }
];

// Teil labels for display
const teilLabels = {
  1: { name: 'Teil 1: Allgemeine Fragen', color: 'bg-green-500', emoji: '🟢', level: 'A2' },
  2: { name: 'Teil 2: Alltag und Erfahrungen', color: 'bg-yellow-500', emoji: '🟡', level: 'A2–B1' },
  3: { name: 'Teil 3: Meinung und Argumentation', color: 'bg-blue-500', emoji: '🔵', level: 'B1–B2' }
};

// Preview questions for animated card
const previewQuestions = [
  { num: 1, level: 'A2', question: "Wie heißt du und woher kommst du?", teil: 1 },
  { num: 7, level: 'A2-B1', question: "Beschreibe deine Schule oder Universität.", teil: 2 },
  { num: 13, level: 'B1-B2', question: "Sollte man im Ausland studieren?", teil: 3 }
];

// Quiz Preview Section Component
const QuizPreviewSection = ({ onStartQuiz }) => {
  const [previewQuestionIndex, setPreviewQuestionIndex] = useState(0);

  useEffect(() => {
    const interval = setInterval(() => {
      setPreviewQuestionIndex((prev) => (prev + 1) % previewQuestions.length);
    }, 3000);
    return () => clearInterval(interval);
  }, []);

  return (
    <section id="test-niveau" className="py-16 md:py-24 bg-gradient-to-br from-primary/5 via-secondary/5 to-accent/5" data-testid="quiz-preview-section">
      <div className="container mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid lg:grid-cols-2 gap-12 items-center">
          {/* Left - Content */}
          <div>
            <span className="inline-block px-4 py-1.5 rounded-full bg-primary/10 text-primary text-sm font-medium mb-4">
              Test gratuit
            </span>
            <h2 className="font-heading text-3xl sm:text-4xl lg:text-5xl font-bold text-gray-900 mb-4">
              Quel est votre niveau d'allemand ?
            </h2>
            <p className="text-lg text-gray-600 mb-6 max-w-lg">
              Testez votre niveau d'allemand en quelques minutes.
            </p>

            {/* Benefits */}
            <div className="space-y-3 mb-6">
              <div className="flex items-center gap-3">
                <CheckCircle className="w-5 h-5 text-green-500 shrink-0" />
                <span className="text-gray-700">Test simple et rapide</span>
              </div>
              <div className="flex items-center gap-3">
                <CheckCircle className="w-5 h-5 text-green-500 shrink-0" />
                <span className="text-gray-700">Résultat instantané</span>
              </div>
              <div className="flex items-center gap-3">
                <CheckCircle className="w-5 h-5 text-green-500 shrink-0" />
                <span className="text-gray-700">Echange dédié (appel, visio ou présentiel)</span>
              </div>
            </div>

            {/* Disclaimer */}
            <div className="inline-flex items-center gap-2 px-4 py-2 bg-yellow-50 border border-yellow-200 rounded-xl text-sm text-yellow-800 mb-8">
              <AlertTriangle className="w-4 h-4 shrink-0" />
              <span>Test indicatif — pas un examen officiel</span>
            </div>

            {/* CTA with social proof */}
            <div className="space-y-4">
              <Button 
                size="lg"
                onClick={onStartQuiz}
                className="bg-primary hover:bg-primary/90 rounded-full px-10 py-7 text-xl font-semibold cta-pulse-btn w-full sm:w-auto"
                data-testid="quiz-start-btn"
              >
                Démarrer le test gratuit
                <ArrowRight className="w-6 h-6 ml-2" />
              </Button>
              
              {/* Social proof counter */}
              <div className="flex items-center gap-3 text-gray-600">
                <div className="flex -space-x-2">
                  <img src="https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?w=100&h=100&fit=crop&crop=face" alt="User" className="w-8 h-8 rounded-full border-2 border-white object-cover" />
                  <img src="https://images.unsplash.com/photo-1494790108377-be9c29b29330?w=100&h=100&fit=crop&crop=face" alt="User" className="w-8 h-8 rounded-full border-2 border-white object-cover" />
                  <img src="https://images.unsplash.com/photo-1539571696357-5a69c17a67c6?w=100&h=100&fit=crop&crop=face" alt="User" className="w-8 h-8 rounded-full border-2 border-white object-cover" />
                </div>
                <div>
                  <span className="font-semibold text-gray-900">114</span> tests déjà réalisés
                </div>
              </div>
            </div>
          </div>

          {/* Right - Animated Quiz Preview Card */}
          <div className="relative">
            <div className="bg-white rounded-3xl shadow-2xl p-6 lg:p-8 border border-gray-100 overflow-hidden">
              {/* Mock progress bar - animated */}
              <div className="flex items-center justify-between mb-4">
                <span className="text-sm text-gray-500 transition-all duration-500">
                  Question {previewQuestions[previewQuestionIndex].num} / 15
                </span>
                <Badge variant="outline" className="text-xs transition-all duration-500">
                  {previewQuestions[previewQuestionIndex].level}
                </Badge>
              </div>
              <Progress 
                value={(previewQuestions[previewQuestionIndex].num / 15) * 100} 
                className="h-2 mb-6 transition-all duration-500" 
              />

              {/* Animated question */}
              <div className="relative min-h-[280px]">
                {previewQuestions.map((q, qIndex) => (
                  <div
                    key={qIndex}
                    className={`absolute inset-0 transition-all duration-500 ${
                      qIndex === previewQuestionIndex 
                        ? 'opacity-100 translate-x-0' 
                        : qIndex < previewQuestionIndex 
                          ? 'opacity-0 -translate-x-8' 
                          : 'opacity-0 translate-x-8'
                    }`}
                  >
                    {/* Teil indicator */}
                    <div className="flex items-center gap-2 mb-3">
                      <span>{teilLabels[q.teil]?.emoji}</span>
                      <span className="text-sm text-gray-500">{teilLabels[q.teil]?.name}</span>
                    </div>
                    <h3 className="text-lg font-semibold text-gray-900 mb-4">
                      {q.question}
                    </h3>
                    {/* Text area preview */}
                    <div className="relative">
                      <textarea 
                        className="w-full p-4 border-2 border-gray-200 rounded-xl bg-gray-50 text-gray-400 resize-none"
                        rows={4}
                        placeholder="Schreiben Sie Ihre Antwort hier..."
                        disabled
                      />
                      <div className="absolute bottom-3 right-3 text-xs text-gray-400">
                        Min. 2-3 Sätze
                      </div>
                    </div>
                  </div>
                ))}
              </div>

              {/* Progress dots */}
              <div className="flex justify-center gap-2 mt-4">
                {previewQuestions.map((_, index) => (
                  <div 
                    key={index}
                    className={`w-2 h-2 rounded-full transition-all duration-300 ${
                      index === previewQuestionIndex ? 'bg-primary w-6' : 'bg-gray-300'
                    }`}
                  />
                ))}
              </div>

              {/* Decorative elements */}
              <div className="absolute -top-4 -right-4 w-24 h-24 bg-primary/10 rounded-full blur-2xl" />
              <div className="absolute -bottom-4 -left-4 w-32 h-32 bg-secondary/10 rounded-full blur-2xl" />
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

// Quiz Overlay Component
const QuizOverlay = ({ 
  showQuiz, 
  quizStep, 
  answers, 
  quizContact, 
  quizResult,
  isQuizSubmitting,
  onClose,
  onBegin,
  onAnswer,
  onNext,
  onPrev,
  onContactChange,
  onSubmitContact
}) => {
  if (!showQuiz) return null;

  return (
    <div className="fixed inset-0 z-50 bg-white overflow-y-auto" data-testid="quiz-overlay">
      {/* Close button */}
      <button 
        onClick={onClose}
        className="fixed top-4 right-4 z-50 w-10 h-10 rounded-full bg-gray-100 hover:bg-gray-200 flex items-center justify-center transition-colors"
        data-testid="quiz-close-btn"
      >
        <X className="w-5 h-5 text-gray-600" />
      </button>

      <div className="min-h-screen flex items-center justify-center px-4 py-8">
        <div className="w-full max-w-2xl">
          
          {/* Step 0 - Introduction */}
          {quizStep === 0 && (
            <div className="text-center animate-in fade-in duration-500">
              <div className="w-20 h-20 rounded-full bg-primary/10 flex items-center justify-center mx-auto mb-6">
                <Target className="w-10 h-10 text-primary" />
              </div>
              <h1 className="font-heading text-3xl sm:text-4xl font-bold text-gray-900 mb-4">
                Quel est votre niveau d'allemand ?
              </h1>
              
              {/* Disclaimer */}
              <div className="bg-yellow-50 border border-yellow-200 rounded-xl p-4 mb-8 text-left max-w-lg mx-auto">
                <div className="flex gap-3">
                  <AlertTriangle className="w-5 h-5 text-yellow-600 shrink-0 mt-0.5" />
                  <div className="text-sm text-yellow-800">
                    <p className="font-medium mb-1">⚠️ Ce test est une auto-évaluation indicative.</p>
                    <p>Il ne remplace pas un test officiel Goethe, TELC ou ECL. Il nous permet simplement de mieux vous orienter lors de votre premier rendez-vous avec notre équipe.</p>
                  </div>
                </div>
              </div>

              <div className="flex items-center justify-center gap-6 mb-8 text-sm text-gray-500">
                <div className="flex items-center gap-2">
                  <Clock className="w-4 h-4" />
                  <span>5 minutes</span>
                </div>
                <div className="flex items-center gap-2">
                  <FileCheck className="w-4 h-4" />
                  <span>15 questions</span>
                </div>
              </div>

              <Button 
                size="lg"
                onClick={onBegin}
                className="bg-primary hover:bg-primary/90 rounded-full px-10 py-6 text-lg"
                data-testid="quiz-begin-btn"
              >
                Commencer le test
                <ArrowRight className="w-5 h-5 ml-2" />
              </Button>
            </div>
          )}

          {/* Steps 1-15 - Questions */}
          {quizStep >= 1 && quizStep <= 15 && (
            <div className="animate-in slide-in-from-right duration-300">
              {/* Progress */}
              <div className="mb-8">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-sm text-gray-500">Question {quizStep} / 15</span>
                  <Badge variant="outline" className="text-xs">
                    {quizQuestions[quizStep - 1].level}
                  </Badge>
                </div>
                <Progress value={(quizStep / 15) * 100} className="h-2" />
              </div>

              {/* Teil indicator */}
              <div className="flex items-center gap-2 mb-4">
                <span>{teilLabels[quizQuestions[quizStep - 1].teil]?.emoji}</span>
                <span className="text-sm font-medium text-gray-600">{teilLabels[quizQuestions[quizStep - 1].teil]?.name}</span>
                <span className="text-xs text-gray-400">({teilLabels[quizQuestions[quizStep - 1].teil]?.level})</span>
              </div>

              {/* Back button */}
              {quizStep > 1 && (
                <button 
                  onClick={onPrev}
                  className="flex items-center gap-1 text-sm text-gray-500 hover:text-gray-700 mb-6 transition-colors"
                  data-testid="quiz-prev-btn"
                >
                  <ChevronLeft className="w-4 h-4" />
                  Question précédente
                </button>
              )}

              {/* Question */}
              <h2 className="text-xl sm:text-2xl font-semibold text-gray-900 mb-6">
                {quizQuestions[quizStep - 1].question}
              </h2>

              {/* Text area for answer */}
              <div className="relative mb-8">
                <textarea 
                  value={answers[quizStep - 1] || ''}
                  onChange={(e) => onAnswer(e.target.value)}
                  className="w-full p-4 border-2 border-gray-200 rounded-xl focus:border-primary focus:ring-2 focus:ring-primary/20 outline-none transition-all resize-none"
                  rows={5}
                  placeholder="Schreiben Sie Ihre Antwort hier... (Écrivez votre réponse en allemand)"
                  data-testid="quiz-text-answer"
                />
                <div className="absolute bottom-3 right-3 text-xs text-gray-400">
                  Min. 2-3 Sätze empfohlen
                </div>
              </div>

              {/* Next button */}
              <Button 
                size="lg"
                onClick={onNext}
                disabled={!answers[quizStep - 1] || answers[quizStep - 1].trim().length < 10}
                className="w-full bg-primary hover:bg-primary/90 rounded-xl py-6 disabled:opacity-50 disabled:cursor-not-allowed"
                data-testid="quiz-next-btn"
              >
                {quizStep === 15 ? 'Soumettre mes réponses' : 'Question suivante'}
                <ArrowRight className="w-5 h-5 ml-2" />
              </Button>
            </div>
          )}

          {/* Step 16 - Contact capture */}
          {quizStep === 16 && (
            <div className="animate-in fade-in duration-500">
              <div className="text-center mb-8">
                <div className="w-20 h-20 rounded-full bg-green-100 flex items-center justify-center mx-auto mb-6">
                  <Sparkles className="w-10 h-10 text-green-600" />
                </div>
                <h2 className="font-heading text-2xl sm:text-3xl font-bold text-gray-900 mb-2">
                  Votre résultat est prêt !
                </h2>
                <p className="text-gray-600">
                  Entrez vos coordonnées pour recevoir votre résultat détaillé et un plan d'études personnalisé
                </p>
              </div>

              <div className="bg-white rounded-2xl border border-gray-200 p-6 shadow-sm">
                <div className="space-y-4">
                  <div className="space-y-2">
                    <Label htmlFor="quiz_prenom">Prénom *</Label>
                    <Input
                      id="quiz_prenom"
                      value={quizContact.prenom}
                      onChange={(e) => onContactChange({ ...quizContact, prenom: e.target.value })}
                      placeholder="Votre prénom"
                      required
                      className="rounded-xl h-12"
                      data-testid="quiz-prenom-input"
                    />
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="quiz_whatsapp">WhatsApp *</Label>
                    <Input
                      id="quiz_whatsapp"
                      type="tel"
                      value={quizContact.whatsapp}
                      onChange={(e) => onContactChange({ ...quizContact, whatsapp: e.target.value })}
                      placeholder="+237 6XX XXX XXX"
                      required
                      className="rounded-xl h-12"
                      data-testid="quiz-whatsapp-input"
                    />
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="quiz_email">Email (optionnel)</Label>
                    <Input
                      id="quiz_email"
                      type="email"
                      value={quizContact.email}
                      onChange={(e) => onContactChange({ ...quizContact, email: e.target.value })}
                      placeholder="votre@email.com"
                      className="rounded-xl h-12"
                      data-testid="quiz-email-input"
                    />
                  </div>

                  <div className="space-y-2">
                    <Label>Ville / Pays (optionnel)</Label>
                    <Select 
                      value={quizContact.ville} 
                      onValueChange={(value) => onContactChange({ ...quizContact, ville: value })}
                    >
                      <SelectTrigger className="rounded-xl h-12" data-testid="quiz-ville-select">
                        <SelectValue placeholder="Sélectionnez votre ville" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="douala">Douala, Cameroun</SelectItem>
                        <SelectItem value="yaounde">Yaoundé, Cameroun</SelectItem>
                        <SelectItem value="dakar">Dakar, Sénégal</SelectItem>
                        <SelectItem value="abidjan">Abidjan, Côte d'Ivoire</SelectItem>
                        <SelectItem value="autre">Autre</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>

                <Button 
                  size="lg"
                  onClick={onSubmitContact}
                  disabled={isQuizSubmitting || !quizContact.prenom || !quizContact.whatsapp}
                  className="w-full bg-primary hover:bg-primary/90 rounded-xl py-6 mt-6"
                  data-testid="quiz-submit-contact-btn"
                >
                  {isQuizSubmitting ? (
                    <>
                      <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                      Chargement...
                    </>
                  ) : (
                    <>
                      Voir mon résultat
                      <ArrowRight className="w-5 h-5 ml-2" />
                    </>
                  )}
                </Button>

                <p className="text-xs text-center text-gray-500 mt-4">
                  Nous ne spammons pas. Promis. 🤝
                </p>
              </div>
            </div>
          )}

          {/* Step 17 - Results */}
          {quizStep === 17 && quizResult && (
            <div className="animate-in fade-in duration-500">
              <div className="text-center mb-8">
                {/* Level Badge - Animated */}
                <div 
                  className={`inline-flex items-center justify-center w-32 h-32 rounded-full ${quizResult.color} text-white mb-6 transform transition-all duration-700 scale-100`}
                  style={{ animation: 'scaleIn 0.6s ease-out' }}
                >
                  <span className="font-heading text-4xl font-bold">{quizResult.level}</span>
                </div>

                <h2 className="font-heading text-2xl sm:text-3xl font-bold text-gray-900 mb-2">
                  Votre niveau : {quizResult.level}
                </h2>
                <p className="text-gray-600 mb-4">
                  Vous avez obtenu <span className="font-semibold">{quizResult.score}/15</span>
                </p>
                <p className="text-gray-600 max-w-md mx-auto">
                  {quizResult.description}
                </p>
              </div>

              {/* Level spectrum */}
              <div className="bg-gray-50 rounded-2xl p-6 mb-6">
                <p className="text-sm text-gray-500 mb-3 text-center">Votre position sur l'échelle</p>
                <div className="flex items-center gap-1 mb-2">
                  {['Débutant', 'A1', 'A2', 'B1', 'B2', 'C1'].map((level, index) => (
                    <div 
                      key={level}
                      className={`flex-1 h-3 rounded-full ${
                        level === quizResult.level 
                          ? quizResult.color 
                          : index < ['Débutant', 'A1', 'A2', 'B1', 'B2', 'C1'].indexOf(quizResult.level)
                            ? 'bg-gray-300'
                            : 'bg-gray-200'
                      }`}
                    />
                  ))}
                </div>
                <div className="flex justify-between text-xs text-gray-500">
                  <span>Débutant</span>
                  <span>C1</span>
                </div>
              </div>

              {/* Recommendation card */}
              <div className="bg-primary/5 border border-primary/20 rounded-2xl p-6 mb-8">
                <h3 className="font-semibold text-gray-900 mb-2 flex items-center gap-2">
                  <Target className="w-5 h-5 text-primary" />
                  Notre recommandation pour vous
                </h3>
                <p className="text-lg font-medium text-primary">
                  {quizResult.recommendation}
                </p>
              </div>

              {/* CTAs */}
              <div className="space-y-3">
                <Button 
                  size="lg"
                  onClick={() => {
                    onClose();
                    document.getElementById('contact')?.scrollIntoView({ behavior: 'smooth' });
                  }}
                  className="w-full bg-primary hover:bg-primary/90 rounded-xl py-6"
                  data-testid="quiz-result-cta"
                >
                  Réserver mon orientation gratuite
                  <ArrowRight className="w-5 h-5 ml-2" />
                </Button>
                <Button 
                  size="lg"
                  variant="outline"
                  onClick={() => window.open('https://wa.me/33761432040?text=Bonjour%20Speak%26Move%2C%20je%20viens%20de%20faire%20le%20test%20de%20niveau%20et%20j\'ai%20obtenu%20' + quizResult.level + '.%20Je%20souhaite%20en%20savoir%20plus.', '_blank')}
                  className="w-full border-2 border-primary text-primary hover:bg-primary/5 rounded-xl py-6"
                  data-testid="quiz-result-whatsapp"
                >
                  💬 Discuter sur WhatsApp
                </Button>
              </div>

              {/* Disclaimer */}
              <p className="text-xs text-center text-gray-500 mt-6">
                ⚠️ Ce test est une auto-évaluation indicative et ne remplace pas un examen officiel.
              </p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

// Main Quiz Hook
export const useQuiz = () => {
  const [showQuiz, setShowQuiz] = useState(false);
  const [quizStep, setQuizStep] = useState(0);
  const [answers, setAnswers] = useState(Array(15).fill(''));
  const [quizStartTime, setQuizStartTime] = useState(null);
  const [quizContact, setQuizContact] = useState({ prenom: '', whatsapp: '', email: '', ville: '' });
  const [isQuizSubmitting, setIsQuizSubmitting] = useState(false);
  const [quizResult, setQuizResult] = useState(null);

  const calculateQuizResult = () => {
    // For text-based quiz, we provide a general result and schedule a consultation
    const answeredCount = answers.filter(a => a && a.trim().length > 10).length;
    
    let level, description, recommendation, color;
    
    // Since it's a written test, we encourage consultation for proper evaluation
    level = 'En évaluation';
    description = `Vous avez répondu à ${answeredCount}/15 questions. Nos experts analyseront vos réponses pour déterminer votre niveau précis.`;
    recommendation = 'Échange personnalisé avec un conseiller pour discuter de vos résultats';
    color = 'bg-primary';

    return { level, description, recommendation, color, score: answeredCount, total: 15, answers };
  };

  const startQuiz = () => {
    setShowQuiz(true);
    setQuizStep(0);
    setAnswers(Array(15).fill(''));
    setQuizStartTime(null);
    setQuizResult(null);
  };

  const beginQuizQuestions = () => {
    setQuizStartTime(Date.now());
    setQuizStep(1);
  };

  const handleQuizAnswer = (text) => {
    const newAnswers = [...answers];
    newAnswers[quizStep - 1] = text;
    setAnswers(newAnswers);
  };

  const handleQuizNext = () => {
    if (answers[quizStep - 1] && answers[quizStep - 1].trim().length >= 10) {
      setQuizStep(quizStep + 1);
    }
  };

  const handleQuizPrev = () => {
    if (quizStep > 1) {
      setQuizStep(quizStep - 1);
    }
  };

  const submitQuizContact = async () => {
    if (!quizContact.prenom || !quizContact.whatsapp) {
      toast.error('Veuillez remplir les champs obligatoires');
      return;
    }

    setIsQuizSubmitting(true);
    const result = calculateQuizResult();
    setQuizResult(result);

    const elapsedTime = quizStartTime ? Math.round((Date.now() - quizStartTime) / 1000) : 0;

    try {
      await leadsApi.create({
        name: quizContact.prenom,
        email: quizContact.email || null,
        whatsapp: quizContact.whatsapp,
        german_level: 'en_evaluation',
        objective: 'quiz_completion',
        message: `Test de niveau complété (${result.score}/15 questions) - Temps: ${elapsedTime}s - Ville: ${quizContact.ville || 'Non renseignée'}\n\nRéponses:\n${answers.map((a, i) => `Q${i+1}: ${a}`).join('\n')}`,
        source: 'quiz_landing'
      });
      setQuizStep(17);
    } catch (error) {
      toast.error("Erreur lors de l'envoi. Veuillez réessayer.");
    } finally {
      setIsQuizSubmitting(false);
    }
  };

  const closeQuiz = () => {
    setShowQuiz(false);
    setQuizStep(0);
    setAnswers(Array(15).fill(''));
    setQuizContact({ prenom: '', whatsapp: '', email: '', ville: '' });
  };

  return {
    showQuiz,
    quizStep,
    answers,
    quizContact,
    quizResult,
    isQuizSubmitting,
    startQuiz,
    beginQuizQuestions,
    handleQuizAnswer,
    handleQuizNext,
    handleQuizPrev,
    setQuizContact,
    submitQuizContact,
    closeQuiz
  };
};

export { QuizPreviewSection, QuizOverlay };
